/**
 * HUD toast lifetime, de-duplication and cap (q-hud-toasts-never-expire; design
 * docs/design/mobile-layout.md §3.1). Pure: every function takes `now` and returns a new
 * list, so the rules are testable without timers. The store (`state/store.ts`) owns the
 * list and the single expiry timer; the HUD (`ui/Hud.tsx`) only renders what is live.
 *
 * Measured defect: a toast lived until clicked, six stacked over the middle of a portrait
 * phone screen, and every refused bar press added another copy.
 */

export type ToastKind = 'info' | 'good' | 'bad';

export interface Toast {
  id: number;
  kind: ToastKind;
  text: string;
  /** When the toast was raised, or last refreshed by a repeat (ms epoch). Its age runs from here. */
  at: number;
  /** De-duplication key: a toast raised with the same key refreshes this one. */
  key: string;
  /** How many times it was raised (shown as ×n when above 1). */
  count: number;
}

/**
 * How long a toast stays on screen, ms, by kind. Long enough to read a line twice; a refusal
 * (`bad`) a little longer because it explains why a press did nothing.
 */
export const TOAST_TTL_MS: Readonly<Record<ToastKind, number>> = {
  info: 4000,
  good: 4000,
  bad: 6000,
};

/** At most this many toasts live at once (the oldest goes first). */
export const TOAST_CAP = 4;

/** The compact (phone) layouts draw only the newest this-many: the rest are under them. */
export const COMPACT_TOAST_ROWS = 2;

/** The key a toast de-duplicates on when the caller names none: same kind, same words. */
export function defaultToastKey(kind: ToastKind, text: string): string {
  return `${kind}\u0000${text}`;
}

/** True while `t` is still on screen at `now`. */
export function toastLive(t: Toast, now: number): boolean {
  return now - t.at < TOAST_TTL_MS[t.kind];
}

/**
 * Raise a toast. A live toast with the same key is refreshed instead of stacking a copy: it
 * takes the new kind and words, its count goes up, its age restarts, and it moves to the
 * newest place (keeping its id, so the row is not re-mounted). Expired toasts are dropped and
 * the list is capped at {@link TOAST_CAP}, oldest first.
 */
export function pushToast(
  list: readonly Toast[],
  raised: { id: number; kind: ToastKind; text: string; key?: string },
  now: number,
): Toast[] {
  const key = raised.key ?? defaultToastKey(raised.kind, raised.text);
  const live = list.filter((t) => toastLive(t, now));
  const prior = live.find((t) => t.key === key);
  const rest = live.filter((t) => t.key !== key);
  const next: Toast = prior
    ? { ...prior, kind: raised.kind, text: raised.text, at: now, count: prior.count + 1 }
    : { id: raised.id, kind: raised.kind, text: raised.text, at: now, key, count: 1 };
  return [...rest, next].slice(-TOAST_CAP);
}

/** The toasts still live at `now` (the same array when none expired). */
export function liveToasts(list: Toast[], now: number): Toast[] {
  return list.every((t) => toastLive(t, now)) ? list : list.filter((t) => toastLive(t, now));
}

/** Ms from `now` until the next toast expires, or null when there is none. */
export function nextToastExpiry(list: readonly Toast[], now: number): number | null {
  let soonest: number | null = null;
  for (const t of list) {
    const left = Math.max(0, t.at + TOAST_TTL_MS[t.kind] - now);
    if (soonest === null || left < soonest) soonest = left;
  }
  return soonest;
}

/** The words a toast row shows: its text, then ×n when it was raised more than once. */
export function toastLabel(t: Pick<Toast, 'text' | 'count'>): string {
  return t.count > 1 ? `${t.text} ×${t.count}` : t.text;
}
